import {
  AfterViewChecked,
  AfterViewInit,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  Input,
  input,
  model,
  NgZone,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import {
  EditorFontFamilyComponent,
  EditorFontSizeComponent,
  EditorFormatComponent,
  EditorResizableOptions,
} from '@progress/kendo-angular-editor';
import { DropDownListComponent } from '@progress/kendo-angular-dropdowns';
import { ToolBarComponent } from '@progress/kendo-angular-toolbar';
import { EditorButtonsConfig } from '@app/pages/main/pages/quality/models';
import { FormControlValueAccessorAdapter } from '@app/shared/utils/form-control-value-accessor.adapter';
import { AbstractControl, FormControl, NG_VALIDATORS, NG_VALUE_ACCESSOR } from '@angular/forms';
import { EditorService } from '@app/pages/main/pages/quality/components/shared/editor/services/editor.service';
import { throttle } from 'lodash';

@Component({
  standalone: false,
  selector: 'ti-editor',
  templateUrl: './editor.component.html',
  styleUrls: ['./editor.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: EditorComponent,
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: EditorComponent,
      multi: true,
    },
  ],
})
export class EditorComponent
  extends FormControlValueAccessorAdapter<string, AbstractControl>
  implements OnInit, AfterViewInit, AfterViewChecked
{
  private editorService = inject(EditorService);
  private elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private zone = inject(NgZone);

  private resizeObserver?: ResizeObserver;
  private toolbarResizeObserver?: ResizeObserver;
  private timeoutId?: ReturnType<typeof setTimeout>;
  private readonly minHeight = signal(0);
  private readonly maxHeight = signal(0);
  private readonly width = signal(0);
  private readonly toolbarHeight = signal(0);
  private readonly gap = signal(0);
  private recalcToolbarOverflow = throttle(() => this.toolbar?.onResize(), 100);

  readonly buttonsConfig = input<EditorButtonsConfig>(this.editorService.getDefaultButtonsConfig());
  @Input() value: string;
  readonly readonly = model<boolean>(false, { alias: 'isReadonly' });
  readonly resizable = input<boolean | 'field' | EditorResizableOptions>('field');

  // Kept apart from `readonly`: a disabled control must not clear an explicit [isReadonly], nor vice versa
  private readonly disabledFromForm = signal(false);
  protected readonly effectiveReadonly = computed(() => this.readonly() || this.disabledFromForm());

  protected readonly resizableValue = computed<boolean | EditorResizableOptions>(() => {
    if (this.effectiveReadonly()) {
      return false;
    }

    if (this.resizable() === 'field') {
      if (!this.width() || !this.minHeight() || !this.maxHeight()) {
        return false;
      }

      return {
        minHeight: this.minHeight() + this.toolbarHeight() + this.gap(),
        maxHeight: this.maxHeight() + this.toolbarHeight() + this.gap(),
        minWidth: this.width(),
        maxWidth: this.width(),
      };
    }

    return this.resizable() as boolean | EditorResizableOptions;
  });

  formatTextPopupClass = 'kendo-format-text-popup';
  fontSizePopupClass = 'kendo-font-size-popup';
  fontFamilyPopupClass = 'kendo-font-family-popup';
  form = new FormControl<string | null>(null);

  @ViewChild('kendoEditorFormat') kendoEditorFormat: EditorFormatComponent;
  @ViewChild('kendoEditorFontSize') kendoEditorFontSize: EditorFontSizeComponent;
  @ViewChild('kendoEditorFontFamily') kendoEditorFontFamily: EditorFontFamilyComponent;
  @ViewChild(ToolBarComponent) private toolbar?: ToolBarComponent;

  constructor() {
    super();

    effect(() => {
      this.setMinAndMaxHeight();
    });
  }

  ngOnInit(): void {
    this.resizeObserver = new ResizeObserver(() => {
      this.width.set(this.elementRef.nativeElement.offsetWidth);
    });
    this.resizeObserver.observe(this.elementRef.nativeElement);

    if (this.value !== undefined) {
      this.initializeValueChangeSubscription();
      this.writeValue(this.value);
    }
  }

  ngAfterViewInit(): void {
    this.zone.runOutsideAngular(() => this.watchToolbarButtonWidth());
  }

  // Only the initial icon render escapes Kendo's own resize sensor; watching the editor would duplicate every recalc
  private watchToolbarButtonWidth(): void {
    const button = this.elementRef.nativeElement.querySelector<HTMLElement>('kendo-toolbar .k-button');
    if (!button) {
      return;
    }

    let lastWidth = button.offsetWidth;
    this.toolbarResizeObserver = new ResizeObserver(() => {
      const width = button.offsetWidth;
      if (!width || width === lastWidth) {
        return;
      }
      lastWidth = width;
      this.recalcToolbarOverflow();
    });
    this.toolbarResizeObserver.observe(button);
  }

  ngAfterViewChecked() {
    this.applyPopupClass(this.kendoEditorFormat?.formatDropDownList?.dropDownList, this.formatTextPopupClass);
    this.applyPopupClass(this.kendoEditorFontSize?.fontSizeDropDownList?.dropDownList, this.fontSizePopupClass);
    this.applyPopupClass(this.kendoEditorFontFamily?.fontFamilyDropDownList?.dropDownList, this.fontFamilyPopupClass);
    if (this.value !== undefined && this.form.value !== this.value) {
      this.writeValue(this.value);
    }
  }

  private applyPopupClass(dropDownList: DropDownListComponent | undefined, popupClass: string): void {
    if (dropDownList && dropDownList.popupSettings.popupClass !== popupClass) {
      dropDownList.popupSettings.popupClass = popupClass;
    }
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabledFromForm.set(isDisabled);
  }

  protected valueModel(): string | undefined {
    return this.form.value ?? undefined;
  }

  protected editorValue(): string {
    const value = this.valueModel();

    if (this.effectiveReadonly()) {
      return value || $localize`No Data`;
    }

    return value || '';
  }

  protected onEditorValueChange(value: string): void {
    if (this.effectiveReadonly()) {
      return;
    }

    this.form.setValue(value);
  }

  private setMinAndMaxHeight(): void {
    if (this.resizable() === 'field') {
      const element = this.elementRef.nativeElement as HTMLElement;

      const toolbar = element.querySelector('kendo-toolbar')!;

      const editor = element.querySelector('kendo-editor')!;
      const toolbarHeight = parseInt(getComputedStyle(toolbar).height, 10);
      const minHeight = parseInt(getComputedStyle(element as Element).minHeight, 10);
      const gap = parseInt(getComputedStyle(editor).gap, 10);
      const maxHeight = parseInt(getComputedStyle(element as Element).maxHeight, 10);

      if (minHeight && maxHeight && ((!this.effectiveReadonly() && toolbarHeight) || this.effectiveReadonly())) {
        this.toolbarHeight.set(this.effectiveReadonly() ? 0 : toolbarHeight);
        this.gap.set(gap);
        this.minHeight.set(minHeight);
        this.maxHeight.set(maxHeight);
      } else {
        clearTimeout(this.timeoutId);
        this.timeoutId = setTimeout(() => this.setMinAndMaxHeight());
      }
    }
  }

  registerOnValidatorChange(fn: () => void): void {}

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.recalcToolbarOverflow.cancel();
    this.resizeObserver?.disconnect();
    this.toolbarResizeObserver?.disconnect();
  }
}
